/**
 * CREATIVE DETONATOR - Local Storage Utilities
 * Phase 2: Store Integration
 *
 * Simple localStorage wrapper with prefix and error handling.
 * Now integrates with central Store for state synchronization.
 *
 * MIGRATION: Use Store.get('dashboard.*') and dashboard actions
 * instead of direct Storage access for reactive state.
 *
 * Extracted from dashboard/core.js for better modularity
 * @version 2.0.0
 */

import { createLogger } from '../../../js/core/logger.js';
import {
  initActions as initDashboardActions,
  setIdeas,
  setSessions,
  setBrands,
  setFilter,
  setSelectedIds,
  setLoading
} from '../../../js/store/actions/dashboard.js';

const log = createLogger('storage');

// ============================================================================
// STORE ACCESS HELPER
// ============================================================================

function getStore() {
  return window.Store;
}

// ============================================================================
// STORAGE MODULE
// ============================================================================

export const Storage = {
  prefix: 'cd_',
  _storeInitialized: false,

  /**
   * Initialize Store actions
   * Call this once when dashboard loads
   */
  initStore() {
    if (this._storeInitialized) return;

    const store = getStore();
    if (store) {
      initDashboardActions();
      this._storeInitialized = true;
      log.debug('Dashboard storage connected to Store');
    }
  },

  /**
   * Set a value in localStorage
   * @param {string} key - Key name (will be prefixed)
   * @param {*} value - Value to store (will be JSON stringified)
   * @returns {boolean} Success status
   */
  set(key, value) {
    try {
      localStorage.setItem(this.prefix + key, JSON.stringify(value));
      return true;
    } catch (e) {
      log.error('Storage set failed:', e.message);
      return false;
    }
  },

  /**
   * Get a value from localStorage
   * @param {string} key - Key name (will be prefixed)
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} The stored value or default
   */
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(this.prefix + key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (e) {
      log.error('Storage get failed:', e.message);
      return defaultValue;
    }
  },

  /**
   * Remove a value from localStorage
   * @param {string} key - Key name (will be prefixed)
   */
  remove(key) {
    localStorage.removeItem(this.prefix + key);
  },

  /**
   * Clear all Creative Detonator data from localStorage
   */
  clear() {
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.prefix))
      .forEach(key => localStorage.removeItem(key));
  },

  /**
   * Check if a key exists in localStorage
   * @param {string} key - Key name (will be prefixed)
   * @returns {boolean}
   */
  has(key) {
    return localStorage.getItem(this.prefix + key) !== null;
  },

  /**
   * Get all keys with the Creative Detonator prefix
   * @returns {string[]} Array of keys (without prefix)
   */
  keys() {
    return Object.keys(localStorage)
      .filter(key => key.startsWith(this.prefix))
      .map(key => key.slice(this.prefix.length));
  },

  // ============================================================================
  // STORE SYNCHRONIZATION (Phase 2)
  // ============================================================================

  /**
   * Load dashboard data from localStorage into Store
   * Call this once on dashboard initialization
   */
  loadIntoStore() {
    this.initStore();

    const store = getStore();
    if (!store) {
      log.warn('Store not available for loading');
      return;
    }

    // Load ideas (with limit to prevent quota issues)
    const ideas = this.get('ideas', []);
    if (Array.isArray(ideas) && ideas.length > 0) {
      // Limit to prevent memory issues
      const limitedIdeas = ideas.slice(-100);
      setIdeas(limitedIdeas);
      log.debug(`Loaded ${limitedIdeas.length} ideas into Store`);
    }

    // Load sessions
    const sessions = this.get('sessions', []);
    if (Array.isArray(sessions) && sessions.length > 0) {
      // Limit sessions to recent ones
      const limitedSessions = sessions.slice(-50);
      setSessions(limitedSessions);
      log.debug(`Loaded ${limitedSessions.length} sessions into Store`);
    }

    // Load brands
    const brands = this.get('brands', []);
    if (Array.isArray(brands) && brands.length > 0) {
      setBrands(brands);
      log.debug(`Loaded ${brands.length} brands into Store`);
    }

    // Load filters
    const filters = this.get('filters', null);
    if (filters) {
      if (filters.format) setFilter('format', filters.format);
      if (filters.saved !== undefined) setFilter('saved', filters.saved);
      if (filters.search) setFilter('search', filters.search);
    }

    log.info('Dashboard data loaded into Store');
  },

  /**
   * Save Store dashboard state to localStorage
   * Call this periodically or on important state changes
   */
  saveFromStore() {
    const store = getStore();
    if (!store) return;

    // Save ideas
    const ideas = store.get('dashboard.ideas') || [];
    if (ideas.length > 0) {
      // Apply limit before saving
      const limitedIdeas = ideas.slice(-100);
      this.set('ideas', limitedIdeas);
    }

    // Save brands (smaller dataset, save all)
    const brands = store.get('dashboard.brands') || [];
    if (brands.length > 0) {
      this.set('brands', brands);
    }

    // Note: Sessions are typically fetched from API, not persisted locally
    // to avoid stale data issues

    // Save filters
    const filters = store.get('dashboard.filters');
    if (filters) {
      this.set('filters', filters);
    }

    log.debug('Dashboard state saved to localStorage');
  },

  /**
   * Sync a specific key between localStorage and Store
   * @param {string} localKey - localStorage key (without prefix)
   * @param {string} storePath - Store path (e.g., 'dashboard.ideas')
   */
  syncKey(localKey, storePath) {
    const store = getStore();
    if (!store) return;

    // Load from localStorage to Store
    const localValue = this.get(localKey);
    if (localValue !== null) {
      store.set(storePath, localValue);
    }
  }
};

// ============================================================================
// LEGACY COMPATIBILITY EXPORTS
// ============================================================================

/**
 * Initialize dashboard storage with Store
 * @deprecated Use Storage.initStore() directly
 */
export function initDashboardStorage() {
  Storage.initStore();
  Storage.loadIntoStore();
}

/**
 * Save dashboard state
 * @deprecated Use Storage.saveFromStore() directly
 */
export function saveDashboardState() {
  Storage.saveFromStore();
}

// Auto-initialize Store connection when this module loads
if (typeof window !== 'undefined' && window.Store) {
  Storage.initStore();
}
